import { FC } from "react";
import { OctagonAlertIcon, TrashIcon } from "lucide-react-native";

import { DocumentTypeEnumType } from "@libs/api/patients";
import { VnBadge } from "@/components/VnBadge/VnBadge";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnSkeleton } from "@/components/VnSkeleton";
import { VnText } from "@/components/VnText/VnText";
import { UploadStatusType } from "@/domain/uploadedDocuments";
import { I18nKeyType } from "@/i18n";
import { dateAsDayjs } from "@/utils/date";

const getDocumentTypeI18nKey = (
  documentType: DocumentTypeEnumType,
): I18nKeyType => {
  return `common:documentTypes.${documentType}` as I18nKeyType;
};

type StatusDisplayPropsType = {
  status: UploadStatusType;
  updatedAt?: string;
};

const StatusDisplay: FC<StatusDisplayPropsType> = ({ status, updatedAt }) => {
  switch (status) {
    case "UPLOADED":
      return (
        <VnBadge>
          <VnText variant="mdMedium" className="text-primary-foreground">
            {updatedAt ? dateAsDayjs(updatedAt).format("MMM D, YYYY") : ""}
          </VnText>
        </VnBadge>
      );

    case "UPLOADING":
    case "SCANNING":
      return (
        <VnBadge variant="secondary" className="flex-row items-center gap-2">
          <VnLoadingSpinner />
          <VnText
            variant="mdMedium"
            i18nKey="native:applicantDocuments.upload.uploading"
          />
        </VnBadge>
      );

    case "FAILED":
      return (
        <VnBox className="gap-2">
          <VnBadge
            variant="destructive"
            className="flex-row items-center gap-2">
            <VnIcon as={OctagonAlertIcon} />
            <VnText
              variant="mdMedium"
              i18nKey="native:applicantDocuments.upload.failed"
            />
          </VnBadge>
          <VnText
            className="text-muted-foreground"
            i18nKey="native:applicantDocuments.upload.failedDescription"
          />
        </VnBox>
      );

    default:
      return null;
  }
};

export const DocumentCardLoadingSkeleton: FC = () => {
  return (
    <>
      <VnCard className="gap-2 p-5">
        <VnBox className="flex-row items-center justify-between pt-2">
          <VnSkeleton className="h-6 w-36" />
        </VnBox>
        <VnSkeleton className="h-5 w-20" />
      </VnCard>
      <VnBox className="h-5 w-full" />
    </>
  );
};

type DocumentCardPropsType = {
  documentType: DocumentTypeEnumType;
  status: UploadStatusType;
  updatedAt?: string;
  onDelete?: () => void;
};

export const DocumentCard: FC<DocumentCardPropsType> = ({
  documentType,
  status,
  updatedAt,
  onDelete,
}) => {
  return (
    <>
      <VnCard className="gap-2 p-5">
        <VnBox className="flex-row items-center justify-between">
          <VnBox className="flex-1 gap-1">
            <VnText
              variant="lg"
              i18nKey={getDocumentTypeI18nKey(documentType)}
            />
          </VnBox>
          {onDelete && (
            <VnButton className="h-8 w-10" variant="ghost" onPress={onDelete}>
              <VnIcon as={TrashIcon} />
            </VnButton>
          )}
        </VnBox>
        <StatusDisplay status={status} updatedAt={updatedAt} />
      </VnCard>
      <VnBox className="h-5 w-full" />
    </>
  );
};
